import { vn } from 'vend-number'
import { PERMISSIONS } from '../../../../constants'
import {
  getOutstandingBalance,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccountLimitReached,
  isPartialPaidSale,
  isReturn,
  isValidCustomer,
} from '../../../../functions/sale'
import {
  useRetailerSettingsManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSaleAsSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { SaleStatus } from '../../../../models/sale.constants'
import { containsGiftCard } from '../../../../functions/lineItem'
import {
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../../../../functions/customer'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { CustomerPaymentOptionsProps } from '../CustomerPaymentOptions'
import { useSaleDisplayStatus } from '../../../util/useSaleDisplayStatus'

export type CustomerPaymentOptionsHookProps = Omit<
  CustomerPaymentOptionsProps,
  'isLoyaltyPaymentDisabled' | 'isStoreCreditPaymentDisabled'
>
/**
 * Build the customer payment buttons:
 * layby, onaccount, loyalty and store credits
 */
export const useCustomerPaymentOptions = (
  props: CustomerPaymentOptionsHookProps
) => {
  const { systemPaymentTypes, pay, laybyOnAccountSale } = props
  const userManager = useUserManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const currentSale = useAppSelector(selectCurrentSaleAsSale)
  const balance = getOutstandingBalance(currentSale)
  const hasNegativeBalance = vn(balance).isNeg()
  const hasValidCustomer = isValidCustomer(currentSale.customer)
  const getSaleDisplayStatus = useSaleDisplayStatus()
  const userCanIssueStoreCredit = userManager.hasPermission(
    PERMISSIONS.STORE_CREDIT_ISSUE_RETURN
  )
  const notReturnSaleAndPositiveBalance =
    !isReturn(currentSale) && !hasNegativeBalance

  const loyaltyPaymentType = systemPaymentTypes.loyaltyPaymentType

  const paymentOptions = []

  // loyalty
  let loyaltyButtonConfig
  if (loyaltyPaymentType && notReturnSaleAndPositiveBalance) {
    loyaltyButtonConfig = {
      id: 'loyalty',
      label: loyaltyPaymentType.name,
      action: () => pay(loyaltyPaymentType),
      isDisabled: false,
    }
    paymentOptions.push(loyaltyButtonConfig)
  }

  // layby payment
  const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
  if (
    notReturnSaleAndPositiveBalance ||
    (hasXeroEnabled && isReturn(currentSale) && !hasNegativeBalance)
  ) {
    if (
      currentSale.displayStatus !== SaleStatus.ACCOUNT_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_LAYBY) &&
      !containsGiftCard(currentSale.lineItems)
    ) {
      const isDisabled =
        !hasValidCustomer ||
        isMarkedAsUnfulfilled(currentSale.status) ||
        isLegacyServiceSale(currentSale.status)
      const laybyButtonConfig = {
        id: SaleStatus.LAYBY_SALE,
        label: getSaleDisplayStatus(SaleStatus.LAYBY_SALE),
        action: () => laybyOnAccountSale(SaleStatus.LAYBY_SALE),
        isDisabled,
      }
      paymentOptions.push(laybyButtonConfig)
    }
  }

  // store credit payment
  const isStoreCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()

  const storeCreditPaymentType = systemPaymentTypes.storeCreditPaymentType
  if (
    isStoreCreditEnabled &&
    storeCreditPaymentType &&
    (!hasNegativeBalance || userCanIssueStoreCredit)
  ) {
    const storeCreditButtonConfig = {
      id: 'storeCredit',
      label: storeCreditPaymentType.name,
      action: () => pay(storeCreditPaymentType),
      isDisabled: false,
    }
    paymentOptions.push(storeCreditButtonConfig)
  }

  // on account payment
  if (notReturnSaleAndPositiveBalance || hasXeroEnabled) {
    if (
      currentSale.displayStatus !== SaleStatus.LAYBY_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_ON_ACCOUNT)
    ) {
      const shouldEnforceOnAccountLimit = getFeatureFromCache(
        'enable_on_account_limit_enforcement'
      )
      const isSavedUnfulfilledWithPayments =
        currentSale.isSavedUnfulfilled && isPartialPaidSale(currentSale)
      const isRestrictedByOnAccountLimit =
        shouldEnforceOnAccountLimit &&
        (isOnAccountLimitDisabled(currentSale.customer) ||
          (!isOnAccountLimitUnlimited(currentSale.customer) &&
            isOnAccountLimitReached(currentSale)))
      const isDisabled =
        !hasValidCustomer ||
        isSavedUnfulfilledWithPayments ||
        isLegacyServiceSale(currentSale.status) ||
        isRestrictedByOnAccountLimit

      const onAccountButtonConfig = {
        id: SaleStatus.ACCOUNT_SALE,
        label: getSaleDisplayStatus(SaleStatus.ACCOUNT_SALE),
        action: () => laybyOnAccountSale(SaleStatus.ACCOUNT_SALE),
        isDisabled,
      }
      paymentOptions.push(onAccountButtonConfig)
    }
  }

  // offline and unable to process this sale with loyalty and store credit
  const isOffline = !webRegisterStatusManager.isOnline()
  const isStoreCreditAndLoyaltyPaymentUnable =
    isOffline &&
    (isStoreCreditEnabled || loyaltyButtonConfig) &&
    hasValidCustomer
  return { paymentOptions, isStoreCreditAndLoyaltyPaymentUnable }
}
